import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../services/api';
import { Plus, X, ShoppingBag, Package, User, Search, Trash2, Edit, Send, AlertCircle, Ban } from 'lucide-react';
import toast from 'react-hot-toast';
import io from 'socket.io-client';
import './Comandas.css';

const __IMPRIMIR_VIA_SOCKET_NO_GARCOM = false;

const __impressoCache = new Map();
const __IMPRESSAO_TTL_MS = 60 * 1000;

const __hashStr = (input) => {
  const str = (input || '').toString();
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
  }
  return (hash >>> 0).toString(36);
};

const __dedupeKey = ({ estabelecimentoId, id, tipo, numero, pedidoNumero, conteudo }) => {
  const est = (estabelecimentoId || '').toString();
  if (id !== undefined && id !== null && `${id}`.length) {
    return `${est}|${id}`;
  }
  const t = (tipo || 'cozinha').toString();
  const n = (numero || pedidoNumero || '').toString();
  const h = __hashStr(conteudo);
  return `${est}|${t}|${n}|${h}`;
};

const __LS_KEY_IMPRESSO = '__garcom_impresso_v1';
const __LS_TTL_MS = 10 * 60 * 1000;
const __lsRead = () => {
  try {
    const raw = localStorage.getItem(__LS_KEY_IMPRESSO);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
};
const __lsWrite = (obj) => {
  try {
    localStorage.setItem(__LS_KEY_IMPRESSO, JSON.stringify(obj));
  } catch (e) {}
};
const __limparLsExpirados = (obj) => {
  const agora = Date.now();
  const novo = {};
  for (const [k, v] of Object.entries(obj || {})) {
    if (typeof v === 'number' && (agora - v) <= __LS_TTL_MS) {
      novo[k] = v;
    }
  }
  return novo;
};
const __jaImpressoGlobal = (chave) => {
  if (typeof window === 'undefined') return false;
  if (!chave) return false;
  const obj = __limparLsExpirados(__lsRead());
  __lsWrite(obj);
  return Boolean(obj[chave]);
};
const __marcarImpressoGlobal = (chave) => {
  if (typeof window === 'undefined') return;
  if (!chave) return;
  const obj = __limparLsExpirados(__lsRead());
  obj[chave] = Date.now();
  __lsWrite(obj);
};

const __marcarImpresso = (chave) => {
  if (!chave) return;
  __impressoCache.set(chave, Date.now());
};
const __jaImpresso = (chave) => {
  if (!chave) return false;
  const ts = __impressoCache.get(chave);
  if (!ts) return false;
  if (Date.now() - ts > __IMPRESSAO_TTL_MS) {
    __impressoCache.delete(chave);
    return false;
  }
  return true;
};

const Comandas = () => {
  const [modalNovaComanda, setModalNovaComanda] = useState(false);
  const [modalAdicionarItem, setModalAdicionarItem] = useState(false);
  const [modalSolicitacao, setModalSolicitacao] = useState(false);
  const [comandaParaSolicitar, setComandaParaSolicitar] = useState(null);
  const [tipoSolicitacao, setTipoSolicitacao] = useState(null); // 'cancelar' ou 'editar'
  const [motivoSolicitacao, setMotivoSolicitacao] = useState('');
  const [modalSolicitacaoRemoverItem, setModalSolicitacaoRemoverItem] = useState(false);
  const [itemParaRemover, setItemParaRemover] = useState(null);
  const [motivoRemocaoItem, setMotivoRemocaoItem] = useState('');
  const [comandaSelecionada, setComandaSelecionada] = useState(null);
  const [novaComanda, setNovaComanda] = useState({ mesa: '', cliente: '', telefone: '' });
  const [novoItem, setNovoItem] = useState({ produto: null, quantidade: 1, clienteItem: '', observacoes: '', acrescimos: [] });
  const [produtoSelecionado, setProdutoSelecionado] = useState(null);
  const [categoriaAtiva, setCategoriaAtiva] = useState(null);
  const queryClient = useQueryClient();

  // ✅ Helper para renderizar nome do cliente (sempre retorna string)
  const getNomeCliente = (cliente) => {
    if (!cliente) return 'Cliente';
    if (typeof cliente === 'string') return cliente;
    if (typeof cliente === 'object' && cliente?.nome) return cliente.nome;
    return 'Cliente';
  };

  // Buscar estabelecimento
  const { data: estabelecimento } = useQuery(
    'estabelecimento-garcom',
    async () => {
      try {
        const response = await api.get('/api/estabelecimentos/me/estabelecimento');
        return response.data.data.estabelecimento;
      } catch (error) {
        if (error.response?.status === 404) {
          try {
            const response = await api.get('/api/estabelecimentos/padrao');
            return response.data.data.estabelecimento;
          } catch {
            return null;
          }
        }
        return null;
      }
    }
  );

  // Buscar produtos
  const { data: produtos } = useQuery(
    ['produtos-garcom', estabelecimento?._id],
    async () => {
      if (!estabelecimento?._id) return [];
      const params = new URLSearchParams();
      params.append('estabelecimento', estabelecimento._id);
      params.append('disponivel', 'true');
      const response = await api.get(`/api/produtos?${params}`);
      return response.data.data.produtos || [];
    },
    { enabled: !!estabelecimento?._id }
  );

  // Buscar adicionais globais
  const { data: adicionaisGlobais } = useQuery(
    ['adicionais-globais-garcom', estabelecimento?._id],
    async () => {
      if (!estabelecimento?._id) return [];
      const response = await api.get(`/api/adicionais?estabelecimento=${estabelecimento._id}&disponivel=true`);
      return response.data.data.adicionais || [];
    },
    {
      enabled: !!estabelecimento?._id,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  );

  // Agrupar produtos por categoria
  const produtosPorCategoria = useMemo(() => {
    if (!produtos || produtos.length === 0) return {};
    const agrupados = {};
    produtos.forEach(produto => {
      const categoria = produto.categoria || 'Outros';
      if (!agrupados[categoria]) {
        agrupados[categoria] = [];
      }
      agrupados[categoria].push(produto);
    });
    return agrupados;
  }, [produtos]);

  // Categorias disponíveis
  const categorias = useMemo(() => {
    return Object.keys(produtosPorCategoria).sort();
  }, [produtosPorCategoria]);

  const adicionaisDisponiveis = useMemo(() => {
    const globais = Array.isArray(adicionaisGlobais) ? adicionaisGlobais : [];
    const doProdutoRaw = Array.isArray(produtoSelecionado?.adicionais)
      ? produtoSelecionado.adicionais
      : [];
    const doProduto = doProdutoRaw.filter((a) => a && a.disponivel !== false);

    const normalizar = (a) => ({
      _id: a?._id,
      nome: a?.nome,
      preco: Number(a?.preco) || 0,
      categoria: a?.categoria || 'Geral'
    });

    const jaExiste = (arr, adicional) => {
      const id = adicional?._id;
      if (id) return arr.some((a) => a?._id === id);
      return arr.some((a) => a?.nome === adicional?.nome && (Number(a?.preco) || 0) === (Number(adicional?.preco) || 0));
    };

    const merged = [];
    doProduto.map(normalizar).filter((a) => a.nome).forEach((a) => {
      if (!jaExiste(merged, a)) merged.push(a);
    });
    globais.map(normalizar).filter((a) => a.nome).forEach((a) => {
      if (!jaExiste(merged, a)) merged.push(a);
    });

    return merged;
  }, [adicionaisGlobais, produtoSelecionado]);

  // Definir categoria ativa quando abrir modal
  useEffect(() => {
    if (modalAdicionarItem && categorias.length > 0 && !categoriaAtiva) {
      setCategoriaAtiva(categorias[0]);
    }
  }, [modalAdicionarItem, categorias, categoriaAtiva]);

  // Resetar categoria quando fechar modal
  useEffect(() => {
    if (!modalAdicionarItem) {
      setCategoriaAtiva(null);
    }
  }, [modalAdicionarItem]);

  // Socket.io para receber comandos de impressão automática (igual iFood/K2)
  useEffect(() => {
    if (!estabelecimento?._id) return;

    // Usar mesma lógica do api.js para determinar URL
    let apiUrl;
    if (process.env.NODE_ENV === 'production') {
      apiUrl = typeof window !== 'undefined' ? window.location.origin : '';
    } else {
      apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    }
    
    const socket = io(apiUrl, {
      transports: ['polling', 'websocket'], // Polling primeiro (mais confiável com proxy)
      path: '/socket.io/',
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionAttempts: 10,
      timeout: 20000, // 20 segundos de timeout
      forceNew: false,
      upgrade: true
    });

    socket.on('connect', () => {
      console.log('✅ Garçom conectado ao Socket.io para impressão automática');
      // Entrar na sala do estabelecimento
      socket.emit('join-estabelecimento', estabelecimento._id);
    });

    socket.on('imprimir-comanda-usb', (dados) => {
      console.log('🖨️ Recebido comando de impressão:', dados.pedidoNumero);

      const chaveDedupe = __dedupeKey({
        estabelecimentoId: dados.estabelecimentoId,
        id: dados.id,
        tipo: dados.tipo,
        numero: dados.numero,
        pedidoNumero: dados.pedidoNumero,
        conteudo: dados.conteudo
      });
      if (__jaImpresso(chaveDedupe) || __jaImpressoGlobal(chaveDedupe)) {
        return;
      }
      
      // Verificar se é do mesmo estabelecimento
      if (dados.estabelecimentoId !== estabelecimento._id.toString()) {
        console.log('⚠️ Comando de outro estabelecimento, ignorando');
        return;
      }

      if (!__IMPRIMIR_VIA_SOCKET_NO_GARCOM) {
        return;
      }

      if (dados.tipo && dados.tipo !== 'cozinha') {
        console.log('⚠️ Comando não é de cozinha, ignorando:', dados.tipo);
        return;
      }

      const larguraImpressora = Number(estabelecimento?.impressora?.largura) || 80;
      const is58 = larguraImpressora === 58;
      const pageMm = is58 ? 58 : 80;
      const bodyMm = is58 ? 44 : 68;
      const fontPrintPx = is58 ? 16 : 17;
      const fontPreviewPx = is58 ? 13 : 14;

      // Criar janela oculta com conteúdo para impressão
      const printWindow = window.open('', '_blank', `width=${pageMm}mm,height=auto`);
      if (!printWindow) {
        console.error('❌ Não foi possível abrir janela de impressão');
        toast.error('Erro ao abrir impressão. Verifique bloqueador de pop-ups.');
        return;
      }

      const escapeHtml = (str) => (str || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

      const normalizarSeparadores = (str) => {
        const alvo = is58 ? 26 : 32;
        return (str || '')
          .replace(/\r\n/g, '\n')
          .replace(/\r/g, '\n')
          .replace(/\n+$/g, '\n')
          .split('\n')
          .map((linha) => {
            const l = (linha || '').toString();
            const t = l.trim();
            if (/^[-=]{20,}$/.test(t)) return t.slice(0, alvo);

            const idx = l.lastIndexOf('R$');
            if (idx !== -1 && l.length > alvo) {
              const valor = l.slice(idx).trim();
              let esquerda = l.slice(0, idx).trimEnd();
              const maxEsq = Math.max(0, alvo - valor.length - 1);
              if (esquerda.length > maxEsq) esquerda = esquerda.slice(0, maxEsq).trimEnd();
              const espacos = Math.max(1, alvo - esquerda.length - valor.length);
              return (esquerda + ' '.repeat(espacos) + valor).slice(0, alvo);
            }

            return l;
          })
          .join('\n');
      };

      // Formatar conteúdo para impressão (preservar quebras de linha)
      const conteudoFormatado = escapeHtml(normalizarSeparadores(dados.conteudo));

      // URL da logo (tentar diferentes formatos)
      const logoUrl = window.location.origin + '/logo-cantinho.png';

      // Escrever HTML na janela com melhor formatação
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Comanda ${dados.numero}</title>
            <meta charset="UTF-8">
            <style>
              @media print {
                @page {
                  size: ${pageMm}mm auto;
                  margin: 0;
                  padding: 0;
                }
                * {
                  -webkit-print-color-adjust: exact !important;
                  color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
                body {
                  margin: 0 !important;
                  padding: 0.5mm 8mm 0.5mm 0.5mm !important;
                  width: ${bodyMm}mm !important;
                  box-sizing: border-box !important;
                  font-family: 'Courier New', monospace !important;
                  font-size: ${fontPrintPx}px !important;
                  font-weight: normal !important;
                  line-height: 1.22 !important;
                  color: #000 !important;
                  background: #fff !important;
                }
                .logo {
                  width: 40mm;
                  height: 40mm;
                  object-fit: contain;
                  margin: 0 auto 2mm;
                  display: block;
                  filter: grayscale(100%) contrast(200%);
                }
                .conteudo {
                  font-size: ${fontPrintPx}px !important;
                  font-weight: 700 !important;
                  text-shadow: 0 0 0.6px #000 !important;
                  color: #000 !important;
                  line-height: 1.22 !important;
                  margin: 0 !important;
                  white-space: pre-wrap !important;
                  overflow-wrap: anywhere !important;
                  word-break: break-word !important;
                }
              }

              body {
                margin: 0 !important;
                padding: 0.5mm 8mm 0.5mm 0.5mm !important;
                width: ${bodyMm}mm !important;
                box-sizing: border-box !important;
                font-family: 'Courier New', monospace !important;
                font-size: ${fontPreviewPx}px !important;
                font-weight: normal !important;
                white-space: pre-wrap;
                line-height: 1.22 !important;
                color: #000 !important;
                background: #fff !important;
              }
              .logo {
                width: 40mm;
                height: 40mm;
                object-fit: contain;
                margin: 0 auto 2mm;
                display: block;
                filter: grayscale(100%) contrast(200%);
              }
              .conteudo {
                font-size: ${fontPreviewPx}px !important;
                font-weight: 700 !important;
                text-shadow: 0 0 0.6px #000 !important;
                color: #000 !important;
                line-height: 1.22 !important;
                margin: 0 !important;
                white-space: pre-wrap !important;
                overflow-wrap: anywhere !important;
                word-break: break-word !important;
              }
            </style>
          </head>
          <body>
            <img src="${logoUrl}" alt="Logo" class="logo" onerror="this.style.display='none';" />
            <pre class="conteudo">${conteudoFormatado}</pre>
            <script>
              (function() {
                let jaImprimiu = false;
                const tentarImprimir = () => {
                  if (jaImprimiu) return;
                  try {
                    jaImprimiu = true;
                    window.focus();
                    window.print();
                  } catch (e) {
                    jaImprimiu = false;
                    setTimeout(tentarImprimir, 300);
                  }
                };
                window.addEventListener('load', () => setTimeout(tentarImprimir, 200));
                window.onafterprint = () => setTimeout(() => window.close(), 100);
              })();
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();

      __marcarImpresso(chaveDedupe);
      __marcarImpressoGlobal(chaveDedupe);
      setTimeout(() => {
        toast.success(`Comanda #${dados.numero} enviada para impressão`, { duration: 2000 });
      }, 500);
    });

    socket.on('disconnect', () => {
      console.log('❌ Garçom desconectado do Socket.io');
    });

    socket.on('connect_error', (error) => {
      console.error('❌ Erro de conexão Socket.io:', error);
    });

    return () => {
      socket.disconnect();
    };
  }, [estabelecimento?._id]);

  // Polling HTTP para verificar comandos de impressão (fallback quando Socket.io não funciona)
  useEffect(() => {
    if (!estabelecimento?._id) return;

    let comandosProcessados = new Set();
    let intervaloId = null;

    const instanciaId = `garcom-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const LOCK_KEY = '__garcom_poller_lock_v1';
    const LOCK_TTL_MS = 5000;
    const getLock = () => {
      try {
        const raw = localStorage.getItem(LOCK_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        return null;
      }
    };
    const setLock = (lock) => {
      try {
        localStorage.setItem(LOCK_KEY, JSON.stringify(lock));
      } catch (e) {}
    };
    const tryAcquire = () => {
      const lock = getLock();
      if (!lock || !lock.id || !lock.ts || (Date.now() - lock.ts > LOCK_TTL_MS) || lock.id === instanciaId) {
        setLock({ id: instanciaId, ts: Date.now() });
        return true;
      }
      return lock.id === instanciaId;
    };

    const escapeHtml = (str) => (str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    const normalizarSeparadores = (str) => {
      const larguraImpressora = Number(estabelecimento?.impressora?.largura) || 80;
      const is58 = larguraImpressora === 58;
      const alvo = is58 ? 26 : 32;
      return (str || '')
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .replace(/\n+$/g, '\n')
        .split('\n')
        .map((linha) => {
          const l = (linha || '').toString();
          const t = l.trim();
          if (/^[-=]{20,}$/.test(t)) return t.slice(0, alvo);

          const idx = l.lastIndexOf('R$');
          if (idx !== -1 && l.length > alvo) {
            const valor = l.slice(idx).trim();
            let esquerda = l.slice(0, idx).trimEnd();
            const maxEsq = Math.max(0, alvo - valor.length - 1);
            if (esquerda.length > maxEsq) esquerda = esquerda.slice(0, maxEsq).trimEnd();
            const espacos = Math.max(1, alvo - esquerda.length - valor.length);
            return (esquerda + ' '.repeat(espacos) + valor).slice(0, alvo);
          }

          return l;
        })
        .join('\n');
    };

    const verificarComandos = async () => {
      try {
        if (!tryAcquire()) {
          return;
        }
        const response = await api.get('/api/agente/comandos.json');
        const dados = response.data;
        const comandos = dados.comandos || [];

        // Se não tiver estabelecimentoId, processar também (comandos antigos)
        const comandosNovos = comandos.filter(cmd => {
          const cmdId = `${cmd.id}-${cmd.timestamp}`;
          const mesmoEstabelecimento = !cmd.estabelecimentoId || cmd.estabelecimentoId === estabelecimento._id.toString();
          const tipoValido = cmd.tipo === 'cozinha';
          return mesmoEstabelecimento && !comandosProcessados.has(cmdId) && tipoValido;
        });

        for (const cmd of comandosNovos) {
          const cmdId = `${cmd.id}-${cmd.timestamp}`;
          comandosProcessados.add(cmdId);

          console.log('🖨️ [POLLING] Comando de impressão encontrado:', cmd.pedidoNumero);

          const chaveDedupe = __dedupeKey({
            estabelecimentoId: cmd.estabelecimentoId,
            id: cmd.id,
            tipo: cmd.tipo,
            numero: cmd.numero,
            pedidoNumero: cmd.pedidoNumero,
            conteudo: cmd.conteudo
          });
          if (__jaImpresso(chaveDedupe) || __jaImpressoGlobal(chaveDedupe)) {
            try {
              await api.post('/api/agente/confirmar', { id: cmd.id });
            } catch (confirmError) {
              console.warn('⚠️ Erro ao confirmar comando:', confirmError);
            }
            continue;
          }

          const larguraImpressora = Number(estabelecimento?.impressora?.largura) || 80;
          const is58 = larguraImpressora === 58;
          const pageMm = is58 ? 58 : 80;
          const bodyMm = is58 ? 44 : 68;
          const fontPrintPx = is58 ? 16 : 17;
          const fontPreviewPx = is58 ? 13 : 14;

          const printWindow = window.open('', '_blank', `width=${pageMm}mm,height=auto`);
          if (!printWindow) {
            console.error('❌ Não foi possível abrir janela de impressão');
            continue;
          }

          const conteudoFormatado = escapeHtml(normalizarSeparadores(cmd.conteudo));

          const logoUrl = window.location.origin + '/logo-cantinho.png';

          printWindow.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <title>Comanda ${cmd.numero}</title>
                <meta charset="UTF-8">
                <style>
                  @media print {
                    @page { size: ${pageMm}mm auto; margin: 0; padding: 0; }
                    * { -webkit-print-color-adjust: exact !important; color-adjust: exact !important; print-color-adjust: exact !important; }
                    body { margin: 0 !important; padding: 0.5mm 8mm 0.5mm 0.5mm !important; width: ${bodyMm}mm !important; box-sizing: border-box !important; font-family: 'Courier New', monospace !important; font-size: ${fontPrintPx}px !important; font-weight: normal !important; line-height: 1.22 !important; color: #000 !important; background: #fff !important; }
                  .logo { max-width: ${bodyMm}mm; max-height: 8mm; width: auto; height: auto; object-fit: contain; margin: 0 auto 2mm; display: block; }
                  .conteudo { font-size: ${fontPrintPx}px !important; font-weight: 700 !important; text-shadow: 0 0 0.6px #000 !important; color: #000 !important; line-height: 1.22 !important; margin: 0 !important; white-space: pre-wrap !important; overflow-wrap: anywhere !important; word-break: break-word !important; }
                }
                body { margin: 0 !important; padding: 0.5mm 8mm 0.5mm 0.5mm !important; width: ${bodyMm}mm !important; box-sizing: border-box !important; font-family: 'Courier New', monospace !important; font-size: ${fontPreviewPx}px !important; font-weight: normal !important; white-space: pre-wrap; line-height: 1.22 !important; color: #000 !important; background: #fff !important; }
                .logo { max-width: ${bodyMm}mm; max-height: 8mm; width: auto; height: auto; object-fit: contain; margin: 0 auto 2mm; display: block; }
                .conteudo { font-size: ${fontPreviewPx}px !important; font-weight: 700 !important; text-shadow: 0 0 0.6px #000 !important; color: #000 !important; line-height: 1.22 !important; margin: 0 !important; white-space: pre-wrap !important; overflow-wrap: anywhere !important; word-break: break-word !important; }
                </style>
              </head>
              <body>
                <img src="${logoUrl}" alt="Logo" class="logo" onerror="this.style.display='none';" />
                <pre class="conteudo">${conteudoFormatado}</pre>
                <script>
                  (function() {
                    let jaImprimiu = false;
                    const tentarImprimir = () => {
                      if (jaImprimiu) return;
                      try {
                        jaImprimiu = true;
                        window.focus();
                        window.print();
                      } catch (e) {
                        jaImprimiu = false;
                        setTimeout(tentarImprimir, 300);
                      }
                    };
                    window.addEventListener('load', () => setTimeout(tentarImprimir, 200));
                    window.onafterprint = () => setTimeout(() => window.close(), 100);
                  })();
                </script>
              </body>
            </html>
          `);
          printWindow.document.close();

          __marcarImpresso(chaveDedupe);
          __marcarImpressoGlobal(chaveDedupe);
          setTimeout(() => {
            toast.success(`Comanda #${cmd.numero} enviada para impressão`, { duration: 2000 });
          }, 500);

          try {
            await api.post('/api/agente/confirmar', { id: cmd.id });
          } catch (confirmError) {
            console.warn('⚠️ Erro ao confirmar comando:', confirmError);
          }
        }
      } catch (error) {
        if (error.response?.status !== 404) {
          console.warn('⚠️ Erro ao verificar comandos (polling):', error.message);
        }
      }
    };

    intervaloId = setInterval(verificarComandos, 2000);
    verificarComandos();

    return () => {
      if (intervaloId) clearInterval(intervaloId);
    };
  }, [estabelecimento?._id]);

  // Buscar comandas abertas
  const { data: comandas, isLoading } = useQuery(
    ['comandas-abertas', estabelecimento?._id],
    async () => {
      if (!estabelecimento?._id) return [];
      const response = await api.get(`/api/comandas?estabelecimento=${estabelecimento._id}&status=aberta`);
      const comandasData = response.data.data.comandas || [];
      // Debug: verificar estrutura das comandas
      const comandasComPedido = comandasData.filter(c => c.pedido);
      if (comandasComPedido.length > 0) {
        console.log('📦 Comandas COM PEDIDO recebidas:', comandasComPedido.map(c => ({
          id: c._id,
          numero: c.numero,
          status: c.status,
          temPedido: !!c.pedido,
          pedidoId: c.pedido?._id || c.pedido,
          pedidoStatus: c.pedido?.status || 'N/A',
          pedidoTipo: typeof c.pedido,
          pedidoCompleto: c.pedido
        })));
      }
      return comandasData;
    },
    {
      enabled: !!estabelecimento?._id,
      refetchInterval: 5000
    }
  );

  // Criar comanda
  const criarComandaMutation = useMutation(
    (data) => api.post('/api/comandas', data),
    {
      onSuccess: () => {
        toast.success('Comanda criada com sucesso!');
        setModalNovaComanda(false);
        setNovaComanda({ mesa: '', cliente: '', telefone: '' });
        queryClient.invalidateQueries(['comandas-abertas']);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao criar comanda');
      }
    }
  );

  // Adicionar item
  const adicionarItemMutation = useMutation(
    ({ comandaId, item }) => api.put(`/api/comandas/${comandaId}/itens`, item),
    {
      onSuccess: () => {
        toast.success('Item adicionado com sucesso!');
        setModalAdicionarItem(false);
        setNovoItem({ produto: null, quantidade: 1, clienteItem: '', observacoes: '', acrescimos: [] });
        setProdutoSelecionado(null);
        setCategoriaAtiva(null);
        queryClient.invalidateQueries(['comandas-abertas']);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao adicionar item');
      }
    }
  );

  // Remover item
  const removerItemMutation = useMutation(
    ({ comandaId, itemId }) => api.delete(`/api/comandas/${comandaId}/itens/${itemId}`),
    {
      onSuccess: () => {
        toast.success('Item removido!');
        queryClient.invalidateQueries(['comandas-abertas']);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao remover item');
      }
    }
  );

  // Enviar para produção
  const enviarProducaoMutation = useMutation(
    (comandaId) => api.post(`/api/comandas/${comandaId}/enviar-producao`),
    {
      onSuccess: async () => {
        toast.success('Comanda enviada para produção e impressa!');
        // Invalidar e forçar refetch para garantir que o pedido seja populado
        await queryClient.invalidateQueries(['comandas-abertas']);
        // Aguardar um pouco e refetch novamente para garantir que o pedido seja populado
        setTimeout(() => {
          queryClient.refetchQueries(['comandas-abertas']);
        }, 500);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao enviar para produção');
      }
    }
  );

  // ✅ NOVO: Verificar se comanda está em produção
  const comandaEmProducao = (comanda) => {
    // Se não tem pedido, não está em produção
    if (!comanda.pedido) {
      return false;
    }
    
    // Verificar se pedido é objeto populado ou apenas ID
    let pedidoStatus = null;
    
    if (typeof comanda.pedido === 'object' && comanda.pedido !== null && comanda.pedido.status) {
      // Pedido está populado e tem status
      pedidoStatus = comanda.pedido.status;
    } else {
      // Pedido não está populado ou não tem status
      // Isso não deveria acontecer se o backend estiver populando corretamente
      return false;
    }
    
    // Verificar se status está em produção
    const statusProducao = ['Confirmado', 'Em preparo'];
    return statusProducao.includes(pedidoStatus);
  };

  // ✅ NOVO: Solicitar cancelamento
  const solicitarCancelamentoMutation = useMutation(
    ({ comandaId, motivo }) => api.post(`/api/comandas/${comandaId}/solicitar-cancelamento`, { motivo }),
    {
      onSuccess: () => {
        toast.success('Solicitação de cancelamento enviada! Aguardando aprovação do administrador.');
        setModalSolicitacao(false);
        setComandaParaSolicitar(null);
        setMotivoSolicitacao('');
        queryClient.invalidateQueries(['comandas-abertas']);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao solicitar cancelamento');
      }
    }
  );

  // ✅ NOVO: Solicitar edição
  const solicitarEdicaoMutation = useMutation(
    ({ comandaId, motivo }) => api.post(`/api/comandas/${comandaId}/solicitar-edicao`, { motivo }),
    {
      onSuccess: () => {
        toast.success('Solicitação de edição enviada! Aguardando aprovação do administrador.');
        setModalSolicitacao(false);
        setComandaParaSolicitar(null);
        setMotivoSolicitacao('');
        queryClient.invalidateQueries(['comandas-abertas']);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao solicitar edição');
      }
    }
  );

  // ✅ NOVO: Abrir modal de solicitação
  const abrirModalSolicitacao = (comanda, tipo) => {
    setComandaParaSolicitar(comanda);
    setTipoSolicitacao(tipo);
    setMotivoSolicitacao('');
    setModalSolicitacao(true);
  };

  // ✅ NOVO: Enviar solicitação
  const enviarSolicitacao = () => {
    if (!comandaParaSolicitar || !tipoSolicitacao) return;
    
    if (tipoSolicitacao === 'cancelar') {
      solicitarCancelamentoMutation.mutate({
        comandaId: comandaParaSolicitar._id,
        motivo: motivoSolicitacao
      });
    } else {
      solicitarEdicaoMutation.mutate({
        comandaId: comandaParaSolicitar._id,
        motivo: motivoSolicitacao
      });
    }
  };

  // ✅ NOVO: Abrir modal para solicitar remoção de item
  const abrirModalSolicitacaoRemoverItem = (comanda, item) => {
    setComandaParaSolicitar(comanda);
    setItemParaRemover(item);
    setMotivoRemocaoItem('');
    setModalSolicitacaoRemoverItem(true);
  };

  // ✅ NOVO: Solicitar remoção de item
  const solicitarRemoverItemMutation = useMutation(
    ({ comandaId, itemId, motivo }) => api.post(`/api/comandas/${comandaId}/solicitar-remover-item`, { itemId, motivo }),
    {
      onSuccess: () => {
        toast.success('Solicitação de remoção de item enviada! Aguardando aprovação do administrador.');
        setModalSolicitacaoRemoverItem(false);
        setItemParaRemover(null);
        setMotivoRemocaoItem('');
        queryClient.invalidateQueries(['comandas-abertas']);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao solicitar remoção de item');
      }
    }
  );

  // ✅ NOVO: Enviar solicitação de remoção de item
  const enviarSolicitacaoRemoverItem = () => {
    if (!comandaParaSolicitar || !itemParaRemover) return;
    
    solicitarRemoverItemMutation.mutate({
      comandaId: comandaParaSolicitar._id,
      itemId: itemParaRemover._id,
      motivo: motivoRemocaoItem
    });
  };

  const handleCriarComanda = () => {
    if (!novaComanda.mesa || !novaComanda.cliente) {
      toast.error('Preencha mesa e nome do cliente');
      return;
    }
    criarComandaMutation.mutate({
      mesa: novaComanda.mesa,
      cliente: novaComanda.cliente,
      telefone: novaComanda.telefone,
      estabelecimento: estabelecimento._id
    });
  };

  const handleAdicionarItem = () => {
    if (!novoItem.produto) {
      toast.error('Selecione um produto');
      return;
    }
    
    // Nome de quem está pedindo é opcional
    
    adicionarItemMutation.mutate({
      comandaId: comandaSelecionada._id,
      item: {
        produto: novoItem.produto._id,
        quantidade: novoItem.quantidade,
        clienteItem: novoItem.clienteItem.trim(), // ✅ Garantir que está sem espaços
        observacoes: novoItem.observacoes || '',
        acrescimos: (novoItem.acrescimos || []).map(a => ({
          ...a,
          quantidade: Math.min(20, Math.max(1, Number(a?.quantidade || 1)))
        }))
      }
    });
  };

  const handleSelecionarProduto = (produto) => {
    setProdutoSelecionado(produto);
    setNovoItem({
      ...novoItem,
      produto: produto,
      acrescimos: []
    });
  };

  const toggleAcrescimo = (acrescimo) => {
    const acrescimosAtuais = novoItem.acrescimos || [];
    const index = acrescimosAtuais.findIndex((a) => {
      if (a?._id && acrescimo?._id) return a._id === acrescimo._id;
      return a?.nome === acrescimo?.nome && (Number(a?.preco) || 0) === (Number(acrescimo?.preco) || 0);
    });
    if (index >= 0) {
      setNovoItem({
        ...novoItem,
        acrescimos: acrescimosAtuais.filter((_, i) => i !== index)
      });
    } else {
      setNovoItem({
        ...novoItem,
        acrescimos: [...acrescimosAtuais, { nome: acrescimo.nome, preco: acrescimo.preco, quantidade: 1 }]
      });
    }
  };

  const atualizarQuantidadeAcrescimo = (acrescimo, novaQuantidade) => {
    const qtd = Math.min(20, Math.max(1, Number(novaQuantidade || 1)));
    setNovoItem(prev => ({
      ...prev,
      acrescimos: (prev.acrescimos || []).map(a => {
        if (acrescimo?._id && a?._id) return a._id === acrescimo._id ? { ...a, quantidade: qtd } : a;
        return a?.nome === acrescimo?.nome ? { ...a, quantidade: qtd } : a;
      })
    }));
  };

  return (
    <div className="comandas-page">
      <div className="comandas-header">
        <div>
          <h2>📋 Comandas Abertas</h2>
          <p>Gerencie as comandas das mesas</p>
        </div>
        <button className="btn-nova-comanda" onClick={() => setModalNovaComanda(true)}>
          <Plus size={20} />
          Nova Comanda
        </button>
      </div>

      {isLoading ? (
        <div className="loading-state">Carregando comandas...</div>
      ) : comandas && comandas.length > 0 ? (
        <div className="comandas-grid">
          {comandas.map(comanda => (
            <div key={comanda._id} className="comanda-card">
              <div className="comanda-header-card">
                <div>
                  <h3>{comanda.mesa}</h3>
                  <p className="comanda-cliente">{getNomeCliente(comanda.cliente)}</p>
                  <span className="comanda-numero">#{comanda.numero}</span>
                </div>
                <div className="comanda-status-badge aberta">Aberta</div>
              </div>

              <div className="comanda-itens">
                {comanda.itens && comanda.itens.length > 0 ? (
                  comanda.itens.map((item, idx) => (
                    <div key={idx} className="item-comanda">
                      <div className="item-info">
                        <strong>{item.quantidade}x</strong>
                        <div>
                          <span className="item-nome">{item.produto?.nome || 'Produto'}</span>
                          {item.clienteItem && (
                            <small className="item-cliente">👤 {item.clienteItem}</small>
                          )}
                          {item.observacoes && (
                            <small className="item-obs">📝 {item.observacoes}</small>
                          )}
                          {item.acrescimos && item.acrescimos.length > 0 && (
                            <small className="item-acrescimos">
                              ➕ {item.acrescimos.map(a => `${Math.min(20, Math.max(1, Number(a?.quantidade || 1)))}x ${a.nome}`).join(', ')}
                            </small>
                          )}
                        </div>
                      </div>
                      <div className="item-acoes">
                        <span className="item-preco">R$ {item.subtotal.toFixed(2)}</span>
                        {/* ✅ Esconder botão de remover se comanda estiver em produção */}
                        {!comandaEmProducao(comanda) ? (
                          <button
                            className="btn-remover-item"
                            onClick={() => removerItemMutation.mutate({ comandaId: comanda._id, itemId: item._id })}
                          >
                            <Trash2 size={16} />
                          </button>
                        ) : (
                          <button
                            className="btn-solicitar-remocao-item"
                            onClick={() => abrirModalSolicitacaoRemoverItem(comanda, item)}
                            style={{
                              background: '#fef3c7',
                              color: '#d97706',
                              border: '1px solid #d97706'
                            }}
                            title="Solicitar remoção (requer aprovação do admin)"
                          >
                            <AlertCircle size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="comanda-vazia">
                    <Package size={32} />
                    <p>Nenhum item adicionado</p>
                  </div>
                )}
              </div>

              <div className="comanda-footer">
                <div className="comanda-total">
                  <span>Total:</span>
                  <strong>R$ {comanda.total.toFixed(2)}</strong>
                </div>
                <div className="comanda-acoes">
                  {/* ✅ Se comanda estiver em produção: pode adicionar itens e solicitar cancelamento/edição */}
                  {comandaEmProducao(comanda) ? (
                    <>
                      <button
                        className="btn-adicionar-item"
                        onClick={() => {
                          setComandaSelecionada(comanda);
                          // ✅ Resetar estado ao abrir modal
                          setProdutoSelecionado(null);
                          setNovoItem({ produto: null, quantidade: 1, clienteItem: '', observacoes: '', acrescimos: [] });
                          setCategoriaAtiva(categorias.length > 0 ? categorias[0] : null);
                          setModalAdicionarItem(true);
                        }}
                      >
                        <Plus size={16} />
                        Adicionar Item
                      </button>
                      <button
                        className="btn-solicitar-cancelamento"
                        onClick={() => abrirModalSolicitacao(comanda, 'cancelar')}
                        style={{
                          background: '#fee2e2',
                          color: '#dc2626',
                          border: '1px solid #dc2626'
                        }}
                      >
                        <Ban size={16} />
                        Solicitar Cancelamento
                      </button>
                      <button
                        className="btn-solicitar-edicao"
                        onClick={() => abrirModalSolicitacao(comanda, 'editar')}
                        style={{
                          background: '#dbeafe',
                          color: '#2563eb',
                          border: '1px solid #2563eb'
                        }}
                      >
                        <Edit size={16} />
                        Solicitar Edição
                      </button>
                    </>
                  ) : (
                    <>
                      {comanda.itens && comanda.itens.length > 0 && (
                        <button
                          className="btn-enviar-producao"
                          onClick={() => enviarProducaoMutation.mutate(comanda._id)}
                          disabled={enviarProducaoMutation.isLoading}
                        >
                          <Send size={16} />
                          {enviarProducaoMutation.isLoading ? 'Enviando...' : 'Enviar para Produção'}
                        </button>
                      )}
                      <button
                        className="btn-adicionar-item"
                        onClick={() => {
                          setComandaSelecionada(comanda);
                          // ✅ Resetar estado ao abrir modal
                          setProdutoSelecionado(null);
                          setNovoItem({ produto: null, quantidade: 1, clienteItem: '', observacoes: '', acrescimos: [] });
                          setCategoriaAtiva(categorias.length > 0 ? categorias[0] : null);
                          setModalAdicionarItem(true);
                        }}
                      >
                        <Plus size={16} />
                        Adicionar Item
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <ShoppingBag size={64} />
          <p>Nenhuma comanda aberta</p>
          <span>Crie uma nova comanda para começar</span>
        </div>
      )}

      {/* Modal Nova Comanda */}
      {modalNovaComanda && (
        <div className="modal-overlay" onClick={() => setModalNovaComanda(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Nova Comanda</h3>
              <button className="btn-close-modal" onClick={() => setModalNovaComanda(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Mesa *</label>
                <input
                  type="text"
                  value={novaComanda.mesa}
                  onChange={(e) => setNovaComanda({ ...novaComanda, mesa: e.target.value })}
                  placeholder="Ex: Mesa 1, Mesa 5, etc"
                  required
                />
              </div>
              <div className="form-group">
                <label>Nome do Cliente *</label>
                <input
                  type="text"
                  value={novaComanda.cliente}
                  onChange={(e) => setNovaComanda({ ...novaComanda, cliente: e.target.value })}
                  placeholder="Nome do cliente"
                  required
                />
              </div>
              <div className="form-group">
                <label>Telefone (opcional)</label>
                <input
                  type="text"
                  value={novaComanda.telefone}
                  onChange={(e) => setNovaComanda({ ...novaComanda, telefone: e.target.value })}
                  placeholder="(00) 00000-0000"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setModalNovaComanda(false)}>
                Cancelar
              </button>
              <button className="btn-primary" onClick={handleCriarComanda} disabled={criarComandaMutation.isLoading}>
                {criarComandaMutation.isLoading ? 'Criando...' : 'Criar Comanda'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Adicionar Item */}
      {modalAdicionarItem && (
        <div className="modal-overlay" onClick={() => setModalAdicionarItem(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Adicionar Item - {comandaSelecionada?.mesa}</h3>
              <button className="btn-close-modal" onClick={() => setModalAdicionarItem(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Produto *</label>
                
                {/* Abas de Categorias */}
                {categorias.length > 1 && (
                  <div className="categorias-tabs">
                    {categorias.map(categoria => (
                      <button
                        key={categoria}
                        className={`categoria-tab ${categoriaAtiva === categoria ? 'active' : ''}`}
                        onClick={() => setCategoriaAtiva(categoria)}
                      >
                        {categoria}
                      </button>
                    ))}
                  </div>
                )}

                {/* Grid de Produtos */}
                <div className="produtos-select-grid">
                  {categoriaAtiva && produtosPorCategoria[categoriaAtiva]?.map(produto => {
                    const preco = produto.precoPromocional ?? produto.preco ?? 0;
                    const precoNum = typeof preco === 'number' ? preco : parseFloat(preco) || 0;
                    const temEstoque = produto.estoque && produto.estoque.quantidade > 0;
                    
                    return (
                      <div
                        key={produto._id}
                        className={`produto-select-card ${produtoSelecionado?._id === produto._id ? 'selected' : ''}`}
                        onClick={() => handleSelecionarProduto(produto)}
                      >
                        <div className="produto-select-info">
                          <strong className="produto-nome">{produto.nome}</strong>
                          <span className="produto-preco">R$ {precoNum.toFixed(2)}</span>
                          {temEstoque && (
                            <span className="produto-estoque-badge">
                              {produto.estoque.quantidade} un
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {(!categoriaAtiva || !produtosPorCategoria[categoriaAtiva] || produtosPorCategoria[categoriaAtiva].length === 0) && (
                    <div className="produtos-empty">
                      <Package size={32} />
                      <p>Nenhum produto disponível nesta categoria</p>
                    </div>
                  )}
                </div>
              </div>

              {produtoSelecionado && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Quantidade</label>
                      <div className="quantidade-controls">
                        <button onClick={() => setNovoItem({ ...novoItem, quantidade: Math.max(1, novoItem.quantidade - 1) })}>
                          -
                        </button>
                        <span>{novoItem.quantidade}</span>
                        <button onClick={() => setNovoItem({ ...novoItem, quantidade: novoItem.quantidade + 1 })}>
                          +
                        </button>
                      </div>
                    </div>
                    <div className="form-group">
                      <label>
                        Nome de quem está pedindo
                      </label>
                      <input
                        type="text"
                        value={novoItem.clienteItem}
                        onChange={(e) => setNovoItem({ ...novoItem, clienteItem: e.target.value })}
                        placeholder="Ex: João, Maria, etc"
                        autoFocus={!!produtoSelecionado}
                      />
                    </div>
                  </div>

                  {/* Adicionais Globais */}
                  {adicionaisDisponiveis.length > 0 && (
                    <div className="form-group">
                      <label>Adicionais (opcional)</label>
                      {(() => {
                        // Agrupar adicionais por categoria
                        const adicionaisPorCategoria = adicionaisDisponiveis.reduce((acc, adicional) => {
                          const categoria = adicional.categoria || 'Geral';
                          if (!acc[categoria]) {
                            acc[categoria] = [];
                          }
                          acc[categoria].push(adicional);
                          return acc;
                        }, {});

                        return Object.entries(adicionaisPorCategoria).map(([categoria, adicionais]) => (
                          <div key={categoria} style={{ marginBottom: '1rem' }}>
                            {Object.keys(adicionaisPorCategoria).length > 1 && (
                              <h4 style={{ fontSize: '0.9rem', fontWeight: '600', color: '#6B7280', marginBottom: '0.5rem' }}>
                                {categoria}
                              </h4>
                            )}
                            <div className="acrescimos-grid">
                              {adicionais.map((adicional) => (
                                <div
                                  key={adicional._id || `${adicional.nome}-${adicional.preco}`}
                                  className={`acrescimo-item ${novoItem.acrescimos && novoItem.acrescimos.find(a => (a._id && adicional._id ? a._id === adicional._id : (a.nome === adicional.nome && (Number(a.preco) || 0) === (Number(adicional.preco) || 0)))) ? 'selected' : ''}`}
                                  onClick={() => toggleAcrescimo({ _id: adicional._id, nome: adicional.nome, preco: Number(adicional.preco) || 0 })}
                                >
                                  <span>{adicional.nome}</span>
                                  <span className="acrescimo-preco">+ R$ {(Number(adicional.preco) || 0).toFixed(2)}</span>
                                  {novoItem.acrescimos && novoItem.acrescimos.find(a => (a._id && adicional._id ? a._id === adicional._id : (a.nome === adicional.nome && (Number(a.preco) || 0) === (Number(adicional.preco) || 0)))) && (
                                    <input
                                      type="number"
                                      min={1}
                                      max={20}
                                      value={novoItem.acrescimos.find(a => (a._id && adicional._id ? a._id === adicional._id : (a.nome === adicional.nome && (Number(a.preco) || 0) === (Number(adicional.preco) || 0))))?.quantidade || 1}
                                      onChange={(e) => atualizarQuantidadeAcrescimo({ _id: adicional._id, nome: adicional.nome, preco: Number(adicional.preco) || 0 }, e.target.value)}
                                      style={{ width: '70px', marginLeft: '0.5rem' }}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  )}

                  <div className="form-group">
                    <label>Observações (opcional)</label>
                    <textarea
                      value={novoItem.observacoes}
                      onChange={(e) => setNovoItem({ ...novoItem, observacoes: e.target.value })}
                      placeholder="Ex: Sem cebola, sem passas, bem passado, etc"
                      rows={3}
                    />
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setModalAdicionarItem(false)}>
                Cancelar
              </button>
              <button
                className="btn-primary"
                onClick={handleAdicionarItem}
                disabled={!produtoSelecionado || !novoItem.clienteItem || !novoItem.clienteItem.trim() || adicionarItemMutation.isLoading}
              >
                {adicionarItemMutation.isLoading ? 'Adicionando...' : 'Adicionar Item'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ NOVO: Modal de Solicitação */}
      {modalSolicitacao && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>
                {tipoSolicitacao === 'cancelar' ? '🔴 Solicitar Cancelamento' : '✏️ Solicitar Edição'}
              </h2>
              <button className="btn-close" onClick={() => setModalSolicitacao(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              <div className="info-comanda-solicitacao">
                <p><strong>Comanda:</strong> {comandaParaSolicitar?.mesa}</p>
                <p><strong>Cliente:</strong> {
                  typeof comandaParaSolicitar?.cliente === 'object' && comandaParaSolicitar?.cliente?.nome
                    ? comandaParaSolicitar.cliente.nome
                    : typeof comandaParaSolicitar?.cliente === 'string'
                    ? comandaParaSolicitar.cliente
                    : 'Cliente'
                }</p>
                <p><strong>Total:</strong> R$ {comandaParaSolicitar?.total?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="form-group">
                <label>
                  {tipoSolicitacao === 'cancelar' 
                    ? 'Motivo do cancelamento' 
                    : 'Motivo da edição'} <span style={{color: '#dc2626'}}>*</span>
                </label>
                <textarea
                  value={motivoSolicitacao}
                  onChange={(e) => setMotivoSolicitacao(e.target.value)}
                  placeholder="Descreva o motivo da solicitação (obrigatório)..."
                  rows={4}
                  required
                  style={{
                    border: `2px solid ${!motivoSolicitacao.trim() ? '#fca5a5' : '#e5e7eb'}`
                  }}
                />
                {!motivoSolicitacao.trim() && (
                  <small style={{color: '#dc2626', display: 'block', marginTop: '0.5rem'}}>
                    O motivo é obrigatório
                  </small>
                )}
              </div>
              <div className="alert-info">
                <AlertCircle size={20} />
                <p>
                  {tipoSolicitacao === 'cancelar'
                    ? 'Esta solicitação será enviada para o administrador. A comanda só será cancelada após aprovação.'
                    : 'Esta solicitação será enviada para o administrador. Após aprovação, você poderá editar a comanda novamente.'}
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => setModalSolicitacao(false)}
                disabled={solicitarCancelamentoMutation.isLoading || solicitarEdicaoMutation.isLoading}
              >
                Cancelar
              </button>
              <button
                className={tipoSolicitacao === 'cancelar' ? 'btn-danger' : 'btn-primary'}
                onClick={enviarSolicitacao}
                disabled={!motivoSolicitacao.trim() || solicitarCancelamentoMutation.isLoading || solicitarEdicaoMutation.isLoading}
                style={{
                  opacity: !motivoSolicitacao.trim() ? 0.5 : 1
                }}
              >
                {solicitarCancelamentoMutation.isLoading || solicitarEdicaoMutation.isLoading
                  ? 'Enviando...'
                  : tipoSolicitacao === 'cancelar'
                  ? 'Solicitar Cancelamento'
                  : 'Solicitar Edição'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ NOVO: Modal de Solicitação de Remoção de Item */}
      {modalSolicitacaoRemoverItem && itemParaRemover && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>⚠️ Solicitar Remoção de Item</h2>
              <button className="btn-close" onClick={() => setModalSolicitacaoRemoverItem(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              <div className="info-comanda-solicitacao">
                <p><strong>Comanda:</strong> {comandaParaSolicitar?.mesa}</p>
                <p><strong>Cliente:</strong> {
                  typeof comandaParaSolicitar?.cliente === 'object' && comandaParaSolicitar?.cliente?.nome
                    ? comandaParaSolicitar.cliente.nome
                    : typeof comandaParaSolicitar?.cliente === 'string'
                    ? comandaParaSolicitar.cliente
                    : 'Cliente'
                }</p>
                <p><strong>Item a remover:</strong> {itemParaRemover.quantidade}x {itemParaRemover.produto?.nome || 'Produto'}</p>
                <p><strong>Valor do item:</strong> R$ {itemParaRemover.subtotal?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="form-group">
                <label>Motivo da remoção <span style={{color: '#dc2626'}}>*</span>:</label>
                <textarea
                  value={motivoRemocaoItem}
                  onChange={(e) => setMotivoRemocaoItem(e.target.value)}
                  placeholder="Descreva o motivo da remoção (obrigatório)..."
                  rows={4}
                  required
                />
                {!motivoRemocaoItem.trim() && (
                  <small style={{color: '#dc2626', display: 'block', marginTop: '0.5rem'}}>
                    O motivo é obrigatório para solicitar remoção de item
                  </small>
                )}
              </div>
              <div className="alert-info">
                <AlertCircle size={20} />
                <p>
                  Esta solicitação será enviada para o administrador. O item só será removido após aprovação.
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => setModalSolicitacaoRemoverItem(false)}
                disabled={solicitarRemoverItemMutation.isLoading}
              >
                Cancelar
              </button>
              <button
                className="btn-warning"
                onClick={enviarSolicitacaoRemoverItem}
                disabled={solicitarRemoverItemMutation.isLoading || !motivoRemocaoItem.trim()}
                style={{
                  background: '#fef3c7',
                  color: '#d97706',
                  border: '1px solid #d97706',
                  opacity: !motivoRemocaoItem.trim() ? 0.5 : 1
                }}
              >
                {solicitarRemoverItemMutation.isLoading ? 'Enviando...' : 'Solicitar Remoção'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Comandas;

